import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";

import Loading_animation_view from "../../loading_animation_view/loading_animation_view";
import {
    get_catalogues_documents,
    upload_catalogues_document,
    delete_catalogues_document,
    downloadCatalogue,
} from "../../../services/for_allServices";
import { TextField } from "@mui/material";

const columns = [
    { id: "name", label: "Document", minWidth: 0 },
    { id: "size", label: "Taille", minWidth: 0 },
    { id: "actions", label: "Actions", minWidth: 0 },
];

function formatBytes(bytes) {
    const b = Number(bytes || 0);
    if (!b) return "-";
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    let v = b;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i++;
    }
    return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

// remove forbidden chars for filenames (minimal: block / and \)
function sanitizeFilename(input) {
    if (!input) return "";
    // remove / and \
    let v = String(input).replace(/[\/\\]/g, "");
    // collapse spaces + trim
    v = v.replace(/\s+/g, " ").trim();
    return v;
}


/** Modal upload PDF */
function UploadModal({ open, onClose, onUpload }) {
    const [file, setFile] = useState(null);
    const [filename, setFilename] = useState("");

    useEffect(() => {
        if (!open) {
            setFile(null);
            setFilename("");
        }
    }, [open]);

    function onPickFile(e) {
        const f = e.target.files?.[0] || null;
        if (!f) return;

        const isPdf =
            f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
        if (!isPdf) {
            alert("Veuillez sélectionner un fichier PDF.");
            e.target.value = "";
            return;
        }

        setFile(f);

        // auto-fill filename from file if empty
        if (!filename) {
            const base = f.name.replace(/\.pdf$/i, "");
            setFilename(sanitizeFilename(base));
        }
    }

    const safeFilename = sanitizeFilename(filename);
    const hasForbidden = filename !== safeFilename;

    const canSave = () => {
        if (!file) return false;
        if (!safeFilename) return false;
        // If user typed / or \, block save (strict)
        if (hasForbidden) return false;
        return true;
    };


    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Ajouter un document</DialogTitle>

            <DialogContent>
                <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "14px" }}>
                    <input type="file" accept="application/pdf" onChange={onPickFile} />
                    <div style={{ marginTop: "8px" }}>
                        {file ? <b>{file.name}</b> : <span>Aucun fichier sélectionné</span>}
                    </div>
                    <TextField
                        variant="standard"
                        label="Nom du fichier (sans /)"
                        value={filename}
                        onChange={(e) => setFilename(e.target.value)}
                        fullWidth
                        helperText={
                            hasForbidden
                                ? "Caractères interdits détectés: '/' ou '\\'. Merci de les supprimer."
                                : "Ex: catalogue_2025 (l'extension .pdf sera ajoutée si absente)"
                        }
                        error={hasForbidden}
                    />
                </div>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} className="submit_but cancel_but" variant="outlined">
                    Annuler
                </Button>
                <Button
                    onClick={() => onUpload({ file, filename: safeFilename })}
                    variant="contained"
                    className="submit_but valide_but"
                    disabled={!canSave()}
                >
                    Enregistrer
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function Catalogues_documents_list() {
    const history = useHistory();
    const [url_updated, setUrl_updated] = useState(null);

    const [rows, setRows] = useState(null);
    const [waiting, setWaiting] = useState(false);

    // upload modal
    const [uploadOpen, setUploadOpen] = useState(false);

    useEffect(() => {
        setRows(null);

        async function load_data() {
            try {
                const res = await get_catalogues_documents();
                setRows(res.data || []);
               //  console.log(res.data);
            } catch (e) {
               //  console.log(e?.response || e);
                setRows([]);
            }
        }

        load_data();
    }, [url_updated, history.location]);

    function openUpload() {
        setUploadOpen(true);
    }

    async function onUpload(payload) {
        try {
            const file = payload?.file;
            const filename = payload?.filename;

            if (!file || !filename) return;

            setWaiting(true);

            const fd = new FormData();
            fd.append("catalogue", file);
            fd.append("filename", filename);

            await upload_catalogues_document(fd);

            setUploadOpen(false);
            setUrl_updated(Date.now());
            setWaiting(false);
        } catch (e) {
           //  console.log(e?.response || e);
            setWaiting(false);
        }
    }

    async function onDelete(row) {
        try {
            const ok = window.confirm(`Supprimer le document : ${row.name} ?`);
            if (!ok) return;

            setWaiting(true);

            await delete_catalogues_document(row.name);

            // update local quickly
            const tmp = [...rows].filter((r) => r.name !== row.name);
            setRows(tmp);

            setWaiting(false);
        } catch (e) {
           //  console.log(e?.response || e);
            setWaiting(false);
        }
    }

    async function onDownload(row) {
        try {
            setWaiting(true);

            const res = await downloadCatalogue(row.name); // row.name = filename
            const blob = new Blob([res.data], { type: "application/pdf" });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = row.name || "catalogue.pdf"; // force filename
            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(url);
            setWaiting(false);
        } catch (e) {
           //  console.log(e?.response || e);
            setWaiting(false);
            alert("Download failed");
        }
    }



    return (
        <>
            {rows === null ? (
                <Loading_animation_view />
            ) : (
                <div className="services_list_container">
                    <div className="services_list_container_header">
                        <div className="title_container">
                            <label>Gestion des documents catalogues</label>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                className={waiting ? "submit_but disabled_button" : "submit_but"}
                                onClick={openUpload}
                                disabled={waiting}
                            >
                                Ajouter
                            </Button>
                        </div>
                    </div>

                    <TableContainer className="services_list_table">
                        <Table aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell key={column.id} align="left">
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {rows.length !== 0 ? (
                                    rows.map((row, id) => (
                                        <TableRow key={"catalogues_document_" + id}>
                                            <TableCell align="left">
                                                <b>{row.name}</b>
                                            </TableCell>

                                            <TableCell align="left">{formatBytes(row.size)}</TableCell>

                                            <TableCell align="left">
                                                <div style={{ display: "flex", gap: "10px" }}>
                                                    <Tooltip title="Télécharger" placement="top">
                                                        <span
                                                            className="downloadIcon"
                                                            onClick={() => onDownload(row)}
                                                        >
                                                            <DownloadIcon
                                                                className="my_icon"
                                                                style={{ cursor: "pointer" }}
                                                            />
                                                        </span>
                                                    </Tooltip>

                                                    <Tooltip title="Supprimer" placement="top">
                                                        <span
                                                            className="deleteIcon"
                                                            onClick={() => onDelete(row)}
                                                        >
                                                            <DeleteIcon
                                                                className="my_icon"
                                                                style={{ cursor: "pointer", color: "#d32f2f" }}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" style={{ padding: "20px" }}>
                                            Aucun document disponible
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            )}

            {uploadOpen ? (
                <UploadModal
                    open={uploadOpen}
                    onClose={() => setUploadOpen(false)}
                    onUpload={onUpload}
                />
            ) : (
                <></>
            )}

            <Backdrop className="waiting_backdrop" open={waiting}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    );
}

export default Catalogues_documents_list;
